import { z } from 'zod'

export const PublicPageMetadata = z.strictObject({
  title: z.string(),
  description: z.string(),
  canonicalPath: z.string(),
  imagePath: z.string(),
  imageAlt: z.string(),
})
export type PublicPageMetadataOutput = z.infer<typeof PublicPageMetadata>

export const genericPublicPageMetadata: PublicPageMetadataOutput = {
  title: 'Honey Show',
  description: 'Honey show results and statistics published by show organizers.',
  canonicalPath: '/',
  imagePath: '/api/public/page-preview.png?path=%2F',
  imageAlt: 'Honey Show — published results and statistics',
}

export type PublicHeadTag = {
  tag: 'title' | 'meta' | 'link'
  attrs: Record<string, string>
  children?: string
}

/** Used by document rendering, Vite and client navigation. URLs never use request headers. */
export function publicPageHeadTags(
  page: PublicPageMetadataOutput,
  origin: string
): PublicHeadTag[] {
  const meta = (attrs: Record<string, string>): PublicHeadTag => ({ tag: 'meta', attrs })
  const tags: PublicHeadTag[] = [
    { tag: 'title', attrs: {}, children: page.title },
    meta({ name: 'description', content: page.description }),
    { tag: 'link', attrs: { rel: 'canonical', href: new URL(page.canonicalPath, origin).href } },
    meta({ property: 'og:type', content: 'website' }),
    meta({ property: 'og:site_name', content: 'Honey Show' }),
    meta({ property: 'og:title', content: page.title }),
    meta({ property: 'og:description', content: page.description }),
    meta({ property: 'og:url', content: new URL(page.canonicalPath, origin).href }),
    meta({ property: 'og:image', content: new URL(page.imagePath, origin).href }),
    meta({ property: 'og:image:type', content: 'image/png' }),
    meta({ property: 'og:image:width', content: '1200' }),
    meta({ property: 'og:image:height', content: '630' }),
    meta({ property: 'og:image:alt', content: page.imageAlt }),
  ]
  return tags.map(tag => ({ ...tag, attrs: { ...tag.attrs, 'data-public-page-head': '' } }))
}

export function escapePublicText(value: string): string {
  return value.replace(
    /[&<>"']/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character]!
  )
}

const headBlock = /<!--public-page-head-->[\s\S]*?<!--\/public-page-head-->/g
export function fillPublicPageHead(html: string, page: PublicPageMetadataOutput, origin: string) {
  if ([...html.matchAll(headBlock)].length !== 1)
    throw new Error('Public web index must contain exactly one public-page-head block')
  const head = publicPageHeadTags(page, origin)
    .map(({ tag, attrs, children }) => {
      const attributes = Object.entries(attrs)
        .map(([name, value]) => ` ${name}="${escapePublicText(value)}"`)
        .join('')
      return tag === 'title'
        ? `<title${attributes}>${escapePublicText(children!)}</title>`
        : `<${tag}${attributes}>`
    })
    .join('\n    ')
  return html.replace(
    headBlock,
    () => `<!--public-page-head-->\n    ${head}\n    <!--/public-page-head-->`
  )
}
